import { ICommandBarItemProps, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';

interface IAddEditRemoveButtonsProps {
  id?: string;
  onClickNew?: () => void;
  newDisabled?: boolean;
  newLabel?: string;
  newTooltip?: string;
  newIcon?: string;
  onClickEdit?: () => void;
  editDisabled?: boolean;
  editLabel?: string;
  editTooltip?: string;
  onClickRemove?: () => void;
  removeDisabled?: boolean;
  removeTooltip?: string;
  onClickSave?: () => void;
  saveDisabled?: boolean;
  onClickDiscard?: () => void;
  discardDisabled?: boolean;
  newItems?: IContextualMenuItem[];
  onNewItemsLoad?: (contextualMenu?: IContextualMenuProps | undefined) => void;
  viewItems?: IContextualMenuItem[];
  afterNewCommandButtonProps?: ICommandBarItemProps[];
  extraCommandButtonProps?: ICommandBarItemProps[];
  overflowCommandBarButtonProps?: ICommandBarItemProps[];
  farCommandBarButtonProps?: ICommandBarItemProps[];
  // shown on the right of the bar, before View (a register's period selectors): View stays the last item
  contentBeforeView?: ReactNode;
  noLeftPadding?: boolean;
  overrideManager?: boolean;
}

const AddEditRemoveButtons = (props: IAddEditRemoveButtonsProps) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);
  const isManager = hasUserFeatureGenericManager(appContext);

  let commandButtons: ICommandBarItemProps[] = [];

  //
  // Default edit buttons are for manager roles only
  //
  if (isManager || props.overrideManager) {
    if (props.onClickSave) {
      commandButtons.push({
        className: 'isoplanner-command-save',
        key: 'save',
        text: t('translation:General.Button.Save'),
        iconProps: { iconName: 'Save' },
        onClick: props.onClickSave,
        disabled: props.saveDisabled,
      });
    }

    if (props.onClickDiscard) {
      commandButtons.push({
        className: 'isoplanner-command-discard',
        key: 'discard',
        text: t('translation:General.Button.Discard'),
        iconProps: { iconName: 'Undo' },
        onClick: props.onClickDiscard,
        disabled: props.discardDisabled,
      });
    }

    if (props.onClickNew) {
      commandButtons.push({
        className: 'isoplanner-command-add',
        key: 'new',
        text: props.newLabel || t('translation:General.Button.Add'),
        title: props.newTooltip,
        iconProps: { iconName: `${props.newIcon || 'Add'}` },
        onClick: props.onClickNew,
        disabled: props.newDisabled,
        split: props.newItems && props.newItems.length > 0,
        subMenuProps:
          props.newItems && props.newItems.length > 0
            ? {
                onMenuOpened: props.onNewItemsLoad,
                items: props.newItems,
              }
            : undefined,
      });
    }

    if (props.onClickEdit) {
      commandButtons.push({
        className: 'isoplanner-command-edit',
        key: 'edit',
        text: props.editLabel || t('translation:General.Button.Edit'),
        iconProps: { iconName: 'Edit' },
        onClick: props.onClickEdit,
        disabled: props.editDisabled,
        title: props.editTooltip,
      });
    }

    if (props.onClickRemove) {
      commandButtons.push({
        className: 'isoplanner-command-remove',
        key: 'remove',
        text: t('translation:General.Button.Remove'),
        iconProps: { iconName: 'Delete' },
        onClick: props.onClickRemove,
        disabled: props.removeDisabled,
        title: props.removeTooltip,
      });
    }
  }

  //
  // Extra buttons right after Add (at the start without it), should be authorized by the caller
  //
  if (props.afterNewCommandButtonProps) {
    const newIndex = commandButtons.findIndex((item) => item.key === 'new');
    commandButtons.splice(newIndex + 1, 0, ...props.afterNewCommandButtonProps);
  }

  //
  // Extra buttons, should be authorized by the caller
  //
  if (props.extraCommandButtonProps) {
    commandButtons = [...commandButtons, ...props.extraCommandButtonProps];
  }

  //
  // The far items should contain view options, so no authorization
  //
  const farItems: IContextualMenuItem[] = [...(props.farCommandBarButtonProps ?? [])];
  const viewItem: IContextualMenuItem | undefined =
    props.viewItems && props.viewItems.length > 0
      ? {
          id: 'commandbar-view',
          key: 'view',
          text: t('translation:General.Button.View'),
          iconProps: { iconName: 'View' },
          subMenuProps: {
            items: props.viewItems,
          },
        }
      : undefined;

  // content before View: the bar, the content and View in a row, View in a bar of its own. A component rendered
  // inside the command bar would also mount in the bar's hidden measuring copy, so the content stays outside it
  if (props.contentBeforeView) {
    return (
      <IPStack horizontal wrap verticalAlign="center">
        <IPStack.Item grow>
          <IPCommandBar
            items={commandButtons}
            id={props.id}
            overflowItems={props.overflowCommandBarButtonProps}
            farItems={farItems}
            styles={{ root: { paddingLeft: props.noLeftPadding ? 0 : undefined } }}
          />
        </IPStack.Item>
        <IPStack.Item>{props.contentBeforeView}</IPStack.Item>
        {viewItem && (
          <IPStack.Item>
            <IPCommandBar items={[]} farItems={[viewItem]} />
          </IPStack.Item>
        )}
      </IPStack>
    );
  }

  if (viewItem) farItems.push(viewItem);
  if (commandButtons.length === 0 && farItems.length === 0) {
    return null;
  } else {
    return (
      <IPCommandBar
        items={commandButtons}
        id={props.id}
        overflowItems={props.overflowCommandBarButtonProps}
        farItems={farItems}
        styles={{ root: { paddingLeft: props.noLeftPadding ? 0 : undefined } }}
      />
    );
  }
};

export default AddEditRemoveButtons;
